// frontend/src/services/adminService.js
import apiClient from './apiClient';

// =============================================================================
// SEGMENT TYPES
// =============================================================================

/**
 * Segment options for filtering admin data
 * @typedef {'all' | 'beta' | 'internal'} SegmentType
 *
 * - 'all': All data (everything)
 * - 'beta': Beta users only (excludes @bidtransparency.com) - real user data
 * - 'internal': Internal only (@bidtransparency.com) - test data
 */

// =============================================================================
// EXISTING ENDPOINTS (with segment support)
// =============================================================================

/**
 * Get platform usage statistics
 * @param {SegmentType} segment - User segment filter
 */
export const getUsageStats = async (segment = 'all') => {
    const response = await apiClient.get('/api/admin/usage-stats', {
        params: { segment }
    });
    return response.data;
};

/**
 * Get email and feedback statistics
 * @param {SegmentType} segment - User segment filter
 */
export const getEmailStats = async (segment = 'all') => {
    const response = await apiClient.get('/api/admin/email-stats', {
        params: { segment }
    });
    return response.data;
};

/**
 * Get bid submission statistics
 * @param {SegmentType} segment - User segment filter
 */
export const getBidStats = async (segment = 'all') => {
    const response = await apiClient.get('/api/admin/bid-stats', {
        params: { segment }
    });
    return response.data;
};

/**
 * Get daily login statistics
 * @param {SegmentType} segment - User segment filter
 */
export const getLoginStats = async (segment = 'all') => {
    const response = await apiClient.get('/api/admin/login-stats', {
        params: { segment }
    });
    return response.data;
};

/**
 * Get GC contact performance statistics
 * @param {SegmentType} segment - User segment filter
 */
export const getGcContactStats = async (segment = 'all') => {
    const response = await apiClient.get('/api/admin/gc-contact-stats', {
        params: { segment }
    });
    return response.data;
};

/**
 * Get daily activity charts for last 30 days
 * @param {SegmentType} segment - User segment filter
 */
export const getDailyActivityCharts = async (segment = 'all') => {
    const response = await apiClient.get('/api/admin/daily-charts', {
        params: { segment }
    });
    return response.data;
};

/**
 * Get comprehensive dashboard data
 * @param {SegmentType} segment - User segment filter
 */
export const getDashboardData = async (segment = 'all') => {
    const response = await apiClient.get('/api/admin/dashboard', {
        params: { segment }
    });
    return response.data;
};

/**
 * Check if current user has super admin access
 * This function will return true if the API call succeeds, false if it fails
 */
export const checkSuperAdminAccess = async () => {
    try {
        await apiClient.get('/api/admin/dashboard');
        return true;
    } catch (error) {
        return false;
    }
};

// =============================================================================
// NEW ENHANCED METRICS ENDPOINTS
// =============================================================================

/**
 * Get corrected feedback statistics (bid-based calculation)
 * A bid counts as "responded" if ANY of its feedback requests received a response
 *
 * @param {SegmentType} segment - User segment filter
 * @param {number} days - Number of days for funnel metrics (default: 30)
 */
export const getCorrectedFeedbackStats = async (segment = 'all', days = 30) => {
    const response = await apiClient.get('/api/admin/corrected-feedback-stats', {
        params: { segment, days }
    });
    return response.data;
};

/**
 * Get time-to-response statistics
 * Calculates the time between feedback request sent and GC response
 *
 * @param {SegmentType} segment - User segment filter
 * @param {number} days - Lookback period in days (default: 90)
 */
export const getTimeToResponse = async (segment = 'all', days = 90) => {
    const response = await apiClient.get('/api/admin/time-to-response', {
        params: { segment, days }
    });
    return response.data;
};

/**
 * Get per-company statistics
 *
 * @param {SegmentType} segment - User segment filter
 * @param {number} days - Lookback period in days for "bidsThisMonth" (default: 30)
 */
export const getPerCompanyStats = async (segment = 'all', days = 30) => {
    const response = await apiClient.get('/api/admin/per-company-stats', {
        params: { segment, days }
    });
    return response.data;
};

/**
 * Get bids per user per month statistics
 *
 * @param {SegmentType} segment - User segment filter
 * @param {number} months - Number of months to include (default: 6)
 */
export const getBidsPerUserMonth = async (segment = 'all', months = 6) => {
    const response = await apiClient.get('/api/admin/bids-per-user-month', {
        params: { segment, months }
    });
    return response.data;
};

// =============================================================================
// SEGMENT HELPER CONSTANTS
// =============================================================================

/**
 * Segment options for UI dropdowns/toggles
 * Note: Order matters - "Beta Users" is first as it's the default
 */
export const SEGMENT_OPTIONS = [
    { value: 'beta', label: 'Beta Users' },
    { value: 'all', label: 'All Data' },
    { value: 'internal', label: 'Internal Only' }
];

/**
 * Get display label for a segment value
 * @param {SegmentType} segment
 * @returns {string}
 */
export const getSegmentLabel = (segment) => {
    const option = SEGMENT_OPTIONS.find(opt => opt.value === segment);
    return option ? option.label : 'All Data';
};

// =============================================================================
// BID SUCCESS ANALYTICS
// =============================================================================

/**
 * Get comprehensive bid success analytics
 * Calculates win rates by various factors and provides insights
 *
 * @param {SegmentType} segment - User segment filter
 * @param {number} days - Lookback period in days (default: 365)
 */
export const getBidSuccessAnalytics = async (segment = 'all', days = 365) => {
    const response = await apiClient.get('/api/admin/bid-success-analytics', {
        params: { segment, days }
    });
    return response.data;
};

/**
 * Days filter options for success analytics
 */
export const DAYS_FILTER_OPTIONS = [
    { value: 90, label: 'Last 90 Days' },
    { value: 180, label: 'Last 180 Days' },
    { value: 365, label: 'Last Year' },
    { value: 0, label: 'All Time' }
];